import { Button, InputCheckbox } from "@sellar-io/components";
import { useState } from "react";
import { Icon, SpinnerLegacy } from "../../../../../components";
import {
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../../components/modal/Dialog";
import { ProductTemplateType } from "@sellar-io/api-v2/types";
import { useProductTemplateList } from "../../../../../hooks/api/v2/productTemplate/useProductTemplateList";
import { InfiniteList } from "../../../../../components/infiniteLoading/InfiniteList";
import { ProductTemplateItem } from "./ProductTemplateItem";

import { capitalize } from "@sellar-io/common";
import { MenuItem } from "../../../../discovery/v2/components/DiscoveryMenuBar";
import { useProductTemplateDeleteMany } from "../../../../../hooks/api/v2/productTemplate/useProductTemplateDeleteMany";
import { Alert } from "../../../../../components/alert/Alert";

type ContainerTypeType = "can" | "keg" | "bottle" | "cask" | "misc";

export const ProductTemplateDialogContent = ({
  onDismiss,
  setTemplate,
  next,
}: {
  onDismiss: () => void;
  setTemplate: (template: ProductTemplateType | undefined) => void;
  next: () => void;
}) => {
  const [editing, setEditing] = useState(false);
  const [deleteTemplates, setDeleteTemplates] = useState<number[]>([]);
  const [containerTypes, setContainerTypes] = useState<ContainerTypeType[]>([]);

  const toggleDeleteTemplate = (id: number | undefined) => {
    if (!id) return;
    setDeleteTemplates((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id],
    );
  };

  const toggleContainerTypes = (type: ContainerTypeType) => {
    setContainerTypes((prev) =>
      prev.includes(type)
        ? prev.filter((item) => item !== type)
        : [...prev, type],
    );
  };

  const submitDeleteTemplates = () => {
    deleteProductTemplatesMutation.mutate(
      { ids: deleteTemplates },
      {
        onSuccess: () => {
          setDeleteTemplates([]);
          setContainerTypes([]);
          setEditing(false);
        },
      },
    );
  };

  const queryParameters = {
    limit: 20,
    query:
      containerTypes.length > 0
        ? [
            {
              type: "containerType",
              value: containerTypes,
              modifier: "IN",
            } as const,
          ]
        : [],
  };

  const deleteProductTemplatesMutation =
    useProductTemplateDeleteMany(queryParameters);

  const productTemplatesQuery = useProductTemplateList(queryParameters);

  const productTemplates =
    productTemplatesQuery.data?.pages.flatMap((page) =>
      page.map((template) => template),
    ) ?? [];

  const hasProductTemplates = productTemplates.length > 0;

  return (
    <>
      <DialogNavigation
        title={`Item templates`}
        description="Choose your preferred template below"
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
      />
      <DialogContent>
        <div className="flex flex-col gap-4">
          <div className="flex gap-2">
            {["can", "keg", "cask", "bottle", "misc"].map((type, idx) => (
              <MenuItem
                onClick={() => toggleContainerTypes(type as ContainerTypeType)}
                key={idx}
                selected={containerTypes.includes(type)}
                size="small"
              >
                {capitalize(type)}
                {type === "misc" && "."}
              </MenuItem>
            ))}
          </div>
          <div
            className="flex cursor-pointer items-center gap-4 rounded-lg border border-zinc-300 p-4 hover:bg-zinc-50"
            onClick={() => {
              setTemplate(undefined);
              next();
            }}
          >
            <figure className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-100">
              <Icon icon={"PLUS"} size={1.1} />
            </figure>
            <div className="flex flex-col gap-1">
              <span className="font-semibold">Create a new item</span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            {deleteProductTemplatesMutation.isError && (
              <Alert
                type="error"
                title="Something went wrong trying to delete your templates"
                message="Try again. If the problem persists then reach out to support."
              />
            )}
            <div className="flex items-center justify-between">
              <span className="font-semibold">All templates</span>
              <Button
                color="transparent"
                size="small"
                onClick={() => setEditing(!editing)}
                disabled={!hasProductTemplates}
              >
                {editing ? "Done" : "Edit list"}
              </Button>
            </div>
            <div className="h-[calc(100vh-390px)] overflow-y-auto md:h-[270px]">
              <div className="flex flex-col rounded-lg border border-zinc-300">
                {!hasProductTemplates && (
                  <p className="p-4 text-sm">No templates found</p>
                )}
                {productTemplatesQuery.isFetched ? (
                  <InfiniteList query={productTemplatesQuery}>
                    {productTemplates.map((template, idx) => (
                      <ProductTemplateItem
                        key={idx}
                        template={template}
                        onClick={() => {
                          if (!editing) {
                            setTemplate(template);
                            next();
                          }
                        }}
                      >
                        {editing ? (
                          <InputCheckbox
                            checked={deleteTemplates.includes(template.id ?? 0)}
                            onChange={() => toggleDeleteTemplate(template.id)}
                          />
                        ) : (
                          <Icon icon="PLUS" size={1.1} />
                        )}
                      </ProductTemplateItem>
                    ))}
                  </InfiniteList>
                ) : (
                  <SpinnerLegacy />
                )}
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogCTA className="flex items-center justify-between">
        <Button color="tertiary" onClick={onDismiss}>
          Cancel
        </Button>
        {editing && (
          <Button
            type="submit"
            color="red"
            onClick={submitDeleteTemplates}
            loading={deleteProductTemplatesMutation.isPending}
            disabled={deleteTemplates.length === 0}
          >
            Delete{" "}
            {deleteTemplates.length > 0 ? `(${deleteTemplates.length})` : ""}
          </Button>
        )}
      </DialogCTA>
    </>
  );
};
